import { CalendarCheck, Clock, Loader2, ShoppingCart } from 'lucide-react';
import { useState } from 'react';
import { useMySubscription, useMySubscriptionHistory, useBillingPlans, useCreateOrder, useVerifyPayment } from '../../api/hooks';
import { SectionHeader, Spinner, EmptyState } from '../../components/ui';
import PendingPaymentsPanel from '../../components/billing/PendingPaymentsPanel';
import { fmtDate } from '../../lib/utils';
import { CHECKOUT_ORDER_INVALID_MESSAGE, isValidCheckoutOrder, loadRazorpayCheckout } from '../../lib/razorpay';

// An interrupted or failed verification call is not evidence that Razorpay
// failed to capture the payment, so the customer is never told the payment
// failed and no second order is created automatically.
const VERIFICATION_PENDING_MESSAGE =
  'Your payment has not been confirmed yet. Do not pay again — if money was debited, use "Pending payments" below to check the status.';

function daysRemaining(validTill) {
  if (!validTill) return null;
  const diff = new Date(validTill + 'Z') - Date.now();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

function isExpired(validTill) {
  if (!validTill) return true;
  return new Date(validTill + 'Z') < Date.now();
}

function SourceBadge({ source }) {
  const map = {
    TRIAL:        'bg-info/10 text-info border-info/30',
    PLAN_ASSIGN:  'bg-bull/10 text-bull border-bull/30',
    PAYMENT:      'bg-amber/10 text-amber border-amber/30',
    ADMIN_ASSIGN: 'bg-warn/10 text-warn border-warn/30',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded border text-2xs font-mono ${map[source] ?? 'text-txt-muted border-bg-border'}`}>
      {source}
    </span>
  );
}

export default function SubscriptionPage() {
  const { data: sub, isLoading, error } = useMySubscription();
  const { data: history = [], isLoading: histLoading } = useMySubscriptionHistory();
  const { data: availablePlans = [], isLoading: plansLoading } = useBillingPlans();
  const { mutateAsync: createOrder } = useCreateOrder();
  const { mutateAsync: verifyPayment } = useVerifyPayment();
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [payError, setPayError] = useState(null);
  const [payNotice, setPayNotice] = useState(null);

  async function handlePayNow(plan) {
    if (payingPlanId) return; // prevent double-click
    setPayingPlanId(plan.id);
    setPayError(null);
    setPayNotice(null);
    try {
      const order = await createOrder({ item_type: 'plan', item_id: plan.id });
      // The merchant key always comes from this order. There is no static
      // frontend key to fall back to, so an incomplete order stops here
      // instead of opening Checkout against the wrong merchant account.
      if (!isValidCheckoutOrder(order)) {
        setPayError(CHECKOUT_ORDER_INVALID_MESSAGE);
        setPayingPlanId(null);
        return;
      }
      let Razorpay;
      try {
        Razorpay = await loadRazorpayCheckout();
      } catch {
        setPayError('Payment provider could not be loaded. Check your connection and try again.');
        setPayingPlanId(null);
        return;
      }
      const rzp = new Razorpay({
        key: order.key_id,
        order_id: order.razorpay_order_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: 'ALGOSYS',
        description: plan.name,
        handler: async (response) => {
          try {
            await verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            // queries auto-invalidated by useVerifyPayment onSuccess
          } catch {
            setPayNotice(VERIFICATION_PENDING_MESSAGE);
          } finally {
            setPayingPlanId(null);
          }
        },
        modal: {
          ondismiss: () => {
            setPayingPlanId(null);
            setPayNotice('Checkout was closed. If you completed a payment, use "Pending payments" below to check whether it has been confirmed.');
          },
        },
      });
      rzp.open();
    } catch (err) {
      setPayError(err.message || 'Could not initiate payment. Please try again.');
      setPayingPlanId(null);
    }
  }

  if (isLoading) return <div className="p-6"><Spinner /></div>;

  const expired = !sub || isExpired(sub.valid_till);
  const days = sub ? daysRemaining(sub.valid_till) : null;

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <SectionHeader title="Subscription" />

      {/* Validity card */}
      <div className={`rounded-lg border p-5 ${expired ? 'border-bear/40 bg-bear/5' : 'border-bull/40 bg-bull/5'}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CalendarCheck size={18} className={expired ? 'text-bear' : 'text-bull'} />
              <span className="text-sm font-medium text-txt-primary">
                {expired ? 'Subscription Expired' : 'Active Subscription'}
              </span>
            </div>
            {sub && (
              <div className="text-xs text-txt-muted">
                Valid till: <span className="text-txt-primary font-mono">{fmtDate(sub.valid_till)}</span>
              </div>
            )}
          </div>
          {!expired && days !== null && (
            <div className="flex items-center gap-1.5 text-bull text-sm font-medium">
              <Clock size={14} />
              {days} day{days !== 1 ? 's' : ''} remaining
            </div>
          )}
        </div>

        {sub?.plan && (
          <div className="mt-3 pt-3 border-t border-bg-border text-xs text-txt-muted space-y-1">
            <div>Plan: <span className="text-txt-primary">{sub.plan.name}</span></div>
            <div>Duration: <span className="text-txt-primary">{sub.plan.duration_months} month{sub.plan.duration_months !== 1 ? 's' : ''}</span></div>
            {sub.plan.details && <div className="text-txt-muted">{sub.plan.details}</div>}
          </div>
        )}

        {expired && (
          <div className="mt-3 text-xs text-bear">
            {sub?.plan?.payment_enabled
              ? (
                <button
                  className="underline text-bull disabled:opacity-50"
                  disabled={payingPlanId === sub.plan.id}
                  onClick={() => handlePayNow(sub.plan)}
                >
                  {payingPlanId === sub.plan.id ? 'Opening payment...' : 'Pay Now to renew'}
                </button>
              )
              : 'Contact your operator to renew your subscription.'}
          </div>
        )}
      </div>

      {/* Pending subscription payments (owner-scoped, paginated by the backend) */}
      <PendingPaymentsPanel itemType="plan" title="Pending payments" />

      {/* History */}
      <div>
        <h3 className="text-sm font-medium text-txt-primary mb-3">Subscription History</h3>
        {histLoading ? (
          <Spinner />
        ) : history.length === 0 ? (
          <EmptyState
            title="No subscription history yet"
            description="Your billing and plan history will appear here after your first purchase."
          />
        ) : (
          <div className="border border-bg-border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-bg-surface border-b border-bg-border">
                <tr>
                  <th className="text-left px-3 py-2 text-txt-muted font-medium">Source</th>
                  <th className="text-left px-3 py-2 text-txt-muted font-medium">Plan</th>
                  <th className="text-left px-3 py-2 text-txt-muted font-medium">Valid From</th>
                  <th className="text-left px-3 py-2 text-txt-muted font-medium">Valid Till</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-border">
                {history.map((row) => (
                  <tr key={row.id} className="hover:bg-bg-surface/50">
                    <td className="px-3 py-2"><SourceBadge source={row.source} /></td>
                    <td className="px-3 py-2 text-txt-muted">{row.plan?.name ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-txt-muted">{fmtDate(row.valid_from)}</td>
                    <td className="px-3 py-2 font-mono text-txt-muted">{fmtDate(row.valid_till)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Available Plans */}
      <div>
        <h3 className="text-sm font-medium text-txt-primary mb-3">Available Plans</h3>
        {payError && (
          <div className="mb-3 text-xs text-bear bg-bear/5 border border-bear/30 rounded px-3 py-2">
            {payError}
          </div>
        )}
        {payNotice && (
          <div className="mb-3 text-xs text-warn bg-warn/5 border border-warn/30 rounded px-3 py-2">
            {payNotice}
          </div>
        )}
        {plansLoading ? (
          <Spinner />
        ) : availablePlans.length === 0 ? (
          <EmptyState
            title="No plans available right now"
            description="Contact your operator to get a plan set up."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {availablePlans.map((plan) => {
              const isPaying = payingPlanId === plan.id;
              return (
                <div key={plan.id} className="border border-bg-border rounded-lg p-4 bg-bg-surface/30 flex flex-col gap-3">
                  <div>
                    <div className="text-sm font-medium text-txt-primary">{plan.name}</div>
                    {plan.details && <div className="text-2xs text-txt-muted mt-0.5">{plan.details}</div>}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="space-y-0.5">
                      <div className="text-txt-muted">
                        Validity: <span className="text-txt-primary font-medium">{plan.license_cost * 30} days</span>
                      </div>
                      <div className="text-txt-muted">
                        Amount: <span className="text-txt-primary font-medium">₹{plan.price}</span>
                      </div>
                    </div>
                    <button
                      className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
                      disabled={!plan.payment_enabled || !!payingPlanId}
                      title={plan.payment_enabled ? 'Pay and subscribe' : 'Contact operator to subscribe'}
                      onClick={() => plan.payment_enabled && handlePayNow(plan)}
                    >
                      {isPaying ? <Loader2 size={12} className="animate-spin" /> : <ShoppingCart size={12} />}
                      {plan.payment_enabled ? (isPaying ? 'Opening...' : 'Subscribe') : 'Contact Operator'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
